<template>
    <div class="photo-attachment-picker" v-if="stationId">
        <button type="button" class="add-photos-btn" v-if="!readonly && !hideTrigger" @click="openFileDialog">
            <label :for="inputId">
                <i class="icon icon-photo"></i>
                <span v-if="!iconOnly">{{ $tc("comments.photos.add") }}</span>
            </label>
        </button>
        <input :id="inputId" type="file" accept="image/jpeg, image/png" multiple class="photo-input" @change="onFilesSelected" />
        <div class="photo-previews" v-if="displayItems.length > 0">
            <div class="photo-preview" v-for="item in displayItems" :key="item.key">
                <img v-if="item.preview" :src="item.preview" alt="" />
                <AuthenticatedPhoto v-else-if="item.url" :url="item.url" />
                <button type="button" class="remove-photo" v-if="!readonly" @click="removeItem(item)">
                    <i class="icon icon-trash"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import _ from "lodash";
import Vue, { PropType } from "vue";
import AuthenticatedPhoto from "@/views/shared/AuthenticatedPhoto.vue";
import { AddedPhoto, PortalNoteMedia } from "@/views/notes/model";
import { uploadAddedPhotos } from "@/views/shared/mediaGallery";
import { SnackbarStyle } from "@/store/modules/snackbar";
import { ActionTypes } from "@/store";

type DisplayItem = {
    key: string;
    id?: number;
    url?: string;
    preview?: string;
    pending?: AddedPhoto;
};

let inputCounter = 0;

export default Vue.extend({
    name: "PhotoAttachmentPicker",
    components: { AuthenticatedPhoto },
    props: {
        stationId: {
            type: Number as PropType<number | null>,
            default: null,
        },
        value: {
            type: Array as PropType<number[]>,
            default: () => [],
        },
        existingMedia: {
            type: Array as PropType<PortalNoteMedia[]>,
            default: () => [],
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        hideTrigger: {
            type: Boolean,
            default: false,
        },
        iconOnly: {
            type: Boolean,
            default: false,
        },
    },
    data(): { pending: AddedPhoto[]; inputId: string; items: PortalNoteMedia[]; isUploading: boolean } {
        inputCounter += 1;
        return {
            pending: [],
            inputId: `photoInput-${inputCounter}`,
            items: [],
            isUploading: false,
        };
    },
    computed: {
        displayItems(): DisplayItem[] {
            const attached = this.items.map((m) => ({
                key: `id-${m.id}`,
                id: m.id,
                url: m.url,
            }));
            const queued = this.pending.map((p, i) => ({
                key: `pending-${p.key}-${i}`,
                preview: typeof p.image === "string" ? p.image : undefined,
                pending: p,
            }));
            return [...attached, ...queued];
        },
    },
    watch: {
        existingMedia: {
            handler(media: PortalNoteMedia[]) {
                this.mergeExistingMedia(media);
            },
            immediate: true,
            deep: true,
        },
        value: {
            handler(ids: number[]) {
                this.syncFromValue(ids ?? []);
            },
        },
    },
    methods: {
        syncFromValue(ids: number[]): void {
            if (ids.length === 0) {
                this.items = [];
                this.pending = [];
                return;
            }
            const idSet = new Set(ids);
            this.items = this.items.filter((m) => idSet.has(m.id));
        },
        mergeExistingMedia(media: PortalNoteMedia[]): void {
            const byId = new Map<number, PortalNoteMedia>();
            for (const m of media || []) {
                byId.set(m.id, m);
            }
            for (const m of this.items) {
                byId.set(m.id, m);
            }
            this.items = Array.from(byId.values());
        },
        openFileDialog(): void {
            const input = this.$el.querySelector(`#${this.inputId}`) as HTMLInputElement;
            if (input) {
                input.click();
            }
        },
        emitValue(): void {
            const ids = this.items.map((m) => m.id);
            if (_.isEqual(ids, this.value)) {
                return;
            }
            this.$emit("input", ids);
        },
        readAddedPhoto(file: File, keyTime: Date): Promise<AddedPhoto | null> {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (e.target?.result) {
                        resolve(new AddedPhoto(file.type, file, e.target.result, keyTime));
                    } else {
                        resolve(null);
                    }
                };
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(file);
            });
        },
        async onFilesSelected(ev: Event): Promise<void> {
            const input = ev.target as HTMLInputElement;
            if (!input.files?.length || !this.stationId) {
                return;
            }
            const files = Array.from(input.files).filter((file) => /^image\/(jpeg|png)$/.test(file.type));
            input.value = "";
            if (files.length === 0) {
                return;
            }
            const baseTime = Date.now();
            const photos = (
                await Promise.all(files.map((file, index) => this.readAddedPhoto(file, new Date(baseTime + index * 1000))))
            ).filter((photo): photo is AddedPhoto => photo != null);
            if (photos.length === 0) {
                return;
            }
            this.pending.push(...photos);
            await this.uploadPending();
        },
        async uploadPending(): Promise<void> {
            if (!this.stationId || this.pending.length === 0 || this.isUploading) {
                return;
            }
            this.isUploading = true;
            let hadFailure = false;
            try {
                while (this.pending.length > 0) {
                    const photo = this.pending.shift() as AddedPhoto;
                    try {
                        const uploaded = await uploadAddedPhotos(this.stationId, [photo], (stationId, key, file) =>
                            this.$services.api.uploadDiscussionMedia(stationId, key, file)
                        );
                        this.items = [...this.items, ...uploaded];
                        this.emitValue();
                    } catch {
                        hadFailure = true;
                    }
                }
            } finally {
                this.isUploading = false;
                if (hadFailure) {
                    this.$store.dispatch(ActionTypes.SHOW_SNACKBAR, {
                        message: this.$tc("comments.photos.uploadFail"),
                        type: SnackbarStyle.fail,
                    });
                }
                if (this.pending.length > 0) {
                    await this.uploadPending();
                }
            }
        },
        async removeItem(item: DisplayItem): Promise<void> {
            if (item.id) {
                try {
                    await this.$services.api.deleteDiscussionMedia(item.id);
                    this.items = this.items.filter((m) => m.id !== item.id);
                    this.emitValue();
                } catch {
                    this.$store.dispatch(ActionTypes.SHOW_SNACKBAR, {
                        message: this.$tc("somethingWentWrong"),
                        type: SnackbarStyle.fail,
                    });
                }
            } else if (item.pending) {
                this.pending = this.pending.filter((p) => p !== item.pending);
            }
        },
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/variables";

.photo-input {
    display: none;
}

.add-photos-btn {
    padding: 0;
    margin: 0;
    border: 0;
    background: transparent;
    cursor: pointer;

    label {
        display: flex;
        align-items: center;
        gap: 6px;
        cursor: pointer;
        font-size: 14px;
        margin: 0;
    }

    .icon {
        font-size: 18px;
        line-height: 1;
    }
}

.photo-previews {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.photo-preview {
    position: relative;
    width: 70px;
    height: 50px;
    background: #e2e4e6;
    border-radius: 2px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.06);

    img,
    ::v-deep .authenticated-photo {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .remove-photo {
        position: absolute;
        top: 2px;
        right: 2px;
        width: 22px;
        height: 22px;
        padding: 0;
        border: none;
        border-radius: 50%;
        background: #fff;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid variables.$color-border;
    }
}
</style>
